import React, { useState, useEffect } from 'react';
import GenericModal from './GenericModal';
import { EsporaiInput, EsporaiTextarea } from 'components';
import { validateUrlRaw } from 'utils/validUrl.js';
import { useAuth } from 'components/context/AuthContext';
import { brandsApi, usersApi } from 'api';
import { toast } from 'react-hot-toast';

const UpdateBusinessDataModal = ({ isOpen, onClose }) => {
  const { auth, updateContextUser } = useAuth();
  const activeBrand = auth?.user?.brands?.find(b => b.id === auth?.user?.active_brand_id) || auth?.user?.active_brand;
  const [brandName, setBrandName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [businessDetails, setBusinessDetails] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [urlError, setUrlError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setBrandName(activeBrand?.name || activeBrand?.business_name || auth?.user?.business_name || '');
      setWebsiteUrl(activeBrand?.website_url || auth?.user?.website_url || '');
      setBusinessDetails(activeBrand?.business_details || auth?.user?.business_details || '');
      setUrlError(null);
    }
  }, [isOpen, activeBrand, auth?.user?.website_url, auth?.user?.business_details, auth?.user?.business_name]);

  const validateUrl = () => {
    if (!websiteUrl || !websiteUrl.trim()) {
      return { valid: true };
    }
    return validateUrlRaw(websiteUrl);
  };

  const handleSubmit = async () => {
    const urlValidation = validateUrl();
    if (!urlValidation.valid) {
      setUrlError(urlValidation.error);
      return;
    }
    if (!websiteUrl?.trim() || !businessDetails?.trim()) {
      toast.error('Completá la URL del sitio web y los detalles del negocio para continuar.');
      return;
    }
    if (activeBrand?.id && !brandName?.trim()) {
      toast.error('El nombre de la marca es requerido.');
      return;
    }
    setUrlError(null);

    setIsUpdating(true);
    try {
      if (activeBrand?.id) {
        await brandsApi.updateBrand(activeBrand.id, {
          name: (brandName?.trim() || activeBrand.name || activeBrand.business_name || 'Mi marca'),
          website_url: websiteUrl.trim() || undefined,
          business_details: businessDetails.trim() || undefined
        });
      } else {
        await usersApi.updateUser({
          websiteUrl: websiteUrl.trim() || '',
          business_details: businessDetails.trim() || ''
        });
      }
      const userResponse = await usersApi.getCurrentUser();
      updateContextUser(userResponse.data);
      toast.success('Datos actualizados correctamente');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar los datos');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  const urlValidation = validateUrl();
  const hasValidBrandName = !activeBrand?.id || brandName?.trim();
  const submitDisabled = !websiteUrl?.trim() || !businessDetails?.trim() || !hasValidBrandName || !urlValidation.valid || isUpdating;

  return (
    <GenericModal
      isOpen={isOpen}
      toggle={handleClose}
      onClose={handleClose}
      title="Datos de la marca incompletos"
      description="Completá o actualizá el nombre de la marca, la URL del sitio web y los detalles para proceder con la creación de campaña."
      showLogo={false}
      closable={!isUpdating}
      showSubmitButton={true}
      showCloseButton={true}
      submitButtonText={isUpdating ? 'Guardando...' : 'Guardar'}
      closeButtonText="Cancelar"
      onSubmit={handleSubmit}
      isSubmitting={isUpdating}
      submitDisabled={submitDisabled}
      width="480px"
      contentPadding="0 2rem 1.5rem 2rem"
    >
      <div className="space-y-4">
        {activeBrand?.id && (
          <div>
            <label htmlFor="brandName" className="block text-sm font-semibold text-white/80 mb-2">
              Nombre de la marca
            </label>
            <EsporaiInput
              id="brandName"
              type="text"
              value={brandName}
              onChange={setBrandName}
              placeholder="Nombre de tu negocio"
              disabled={isUpdating}
              maxLength={100}
              className="!bg-slate-900 !text-white placeholder-white/50 caret-white !border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
              style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
            />
          </div>
        )}
        <div>
          <label htmlFor="websiteUrl" className="block text-sm font-semibold text-white/80 mb-2">
            URL del sitio web
          </label>
          <EsporaiInput
            id="websiteUrl"
            type="text"
            value={websiteUrl}
            onChange={(val) => {
              setWebsiteUrl(val);
              setUrlError(null);
            }}
            placeholder="www.minegocio.com"
            error={urlError}
            disabled={isUpdating}
            className={`!bg-slate-900 !text-white placeholder-white/50 caret-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark] ${
              urlError ? '!border-red-500' : '!border-white/30'
            }`}
            style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
          />
        </div>
        <div>
          <label htmlFor="businessDetails" className="block text-sm font-semibold text-white/80 mb-2">
            Detalles de la marca
          </label>
          <EsporaiTextarea
            id="businessDetails"
            value={businessDetails}
            onChange={setBusinessDetails}
            placeholder="Describí tu negocio, productos o servicios..."
            disabled={isUpdating}
            rows={4}
            className="!bg-slate-900 !text-white placeholder-white/50 caret-white !border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
            style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
          />
        </div>
      </div>
    </GenericModal>
  );
};

export default UpdateBusinessDataModal;
